"use client";

import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { GridRowSelectionModel, GridRowId } from "@mui/x-data-grid-pro";
import { campaignAssigneesOptions } from "@/queryOptions/campaignAssignees";

interface UnassignDialogContextType {
  campaignIdsToUnassign: GridRowId[];
  closeUnassignDialog: () => void;
  openUnassignDialog: (campaignIds: GridRowId[]) => void;
  setUserRowSelectionModel: (model: GridRowSelectionModel) => void;
  unassignDialogOpen: boolean;
  userRowSelectionModel: GridRowSelectionModel;
  usersToUnassign: {
    email: string;
    id: number;
  }[];
}

const UnassignDialogContext = createContext<
  UnassignDialogContextType | undefined
>(undefined);

export const useUnassignDialog = () => {
  const context = useContext(UnassignDialogContext);
  if (!context) {
    throw new Error(
      "useUnassignDialog must be used within UnassignDialogProvider",
    );
  }
  return context;
};

interface UnassignDialogProviderProps {
  children: ReactNode;
}

export function UnassignDialogProvider({
  children,
}: UnassignDialogProviderProps) {
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [campaignIdsToUnassign, setCampaignIdsToUnassign] = useState<
    GridRowId[]
  >([]);

  const { data: usersToUnassign = [] } = useQuery({
    ...campaignAssigneesOptions(campaignIdsToUnassign),
    enabled: unassignDialogOpen && campaignIdsToUnassign.length > 0,
  });

  const [userRowSelectionModel, setUserRowSelectionModel] =
    useState<GridRowSelectionModel>({
      ids: new Set<GridRowId>([]),
      type: "include",
    });

  const openUnassignDialog = useCallback((campaignIds: GridRowId[]) => {
    setCampaignIdsToUnassign(campaignIds);
    setUnassignDialogOpen(true);
  }, []);

  const closeUnassignDialog = useCallback(() => {
    setUnassignDialogOpen(false);
  }, []);

  const value: UnassignDialogContextType = {
    campaignIdsToUnassign,
    closeUnassignDialog,
    openUnassignDialog,
    setUserRowSelectionModel,
    unassignDialogOpen,
    userRowSelectionModel,
    usersToUnassign,
  };

  return (
    <UnassignDialogContext.Provider value={value}>
      {children}
    </UnassignDialogContext.Provider>
  );
}

